import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { FormControl, FormGroup } from "@angular/forms";
import { DiaryFormService } from "../../../../core/services/diary-form.service";
import { SelectOption } from "../../../../core/models/select-option.model";
import { DiaryForm } from "../../../../core/models/trial-diary-form/diary-form.model";
import { AlertService } from "../../../../shared/alert/alert.service";
import { LogHelper } from "../../../../core/helpers/log.helper";
import { DiaryFormElementType } from "../../../../core/constants/diary-form-element-type.enum";
import { DiaryType } from "../../../../core/constants/diary-form-type.enum";
import { DropdownInputComponent } from "../../../../shared/dropdown-input/dropdown-input.component";
import { ConfirmActionModalComponent } from "../../../../shared/confirm-action-modal/confirm-action-modal.component";
import { ElementSettingsModalComponent } from "./element-settings-modal/element-settings-modal.component";
import { DiaryFormElement } from "../../../../core/models/trial-diary-form/diary-form-element.model";
import { TrialDiaryConfigModalComponent } from "./trial-diary-config-modal/trial-diary-config-modal.component";
import { TrialDiaryAddRuleModalComponent } from "./trial-diary-add-rule-modal/trial-diary-add-rule-modal.component";
import { DiaryFormElementMessage } from "../../../../core/models/trial-diary-form/diary-form-element-message.model";
import { ElementMessageModalComponent } from "./element-message-modal/element-message-modal.component";
import { ElementVisibilityModalComponent } from "./element-visibility-modal/element-visibility-modal.component";
import {
  DiaryFormElementVisibilityCondition
} from "../../../../core/models/trial-diary-form/diary-form-element-visibility-condition.model";

@Component({
  selector: 'app-trial-diary-form',
  templateUrl: './trial-diary-form.component.html',
  styleUrl: './trial-diary-form.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class TrialDiaryFormComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('addQuestionDropdown') addQuestionDropdown: DropdownInputComponent;
  @ViewChild('confirmRemoveElement') confirmRemoveElement: ConfirmActionModalComponent;
  @ViewChild('confirmRemoveDiary') confirmRemoveDiary: ConfirmActionModalComponent;
  @ViewChild('elementSettingsModal') elementSettingsModal: ElementSettingsModalComponent;
  @ViewChild('configModal') configModal: TrialDiaryConfigModalComponent;
  @ViewChild('addRuleModal') addRuleModal: TrialDiaryAddRuleModalComponent;
  @ViewChild('messageModal') messageModal: ElementMessageModalComponent;
  @ViewChild('visibilityModal') visibilityModal: ElementVisibilityModalComponent;

  @Output() goBack = new EventEmitter();
  @Output() formChanged = new EventEmitter<boolean>();
  @Output() diaryDeleted = new EventEmitter<string>();
  @Output() diaryRenamed = new EventEmitter<string>();

  @Input() formId: string;
  @Input() isLastDiary: boolean = false;

  protected readonly DiaryType = DiaryType;
  protected diaryForm: DiaryForm;
  private initialFormValue: any;
  private initialDiaryFormValue: any;
  formHasChanges = false;
  protected readonly completionWarningElements = new Set<DiaryFormElement>();

  private readonly _diaryFormService = inject(DiaryFormService);
  private readonly _alertService = inject(AlertService);

  protected isLoading = false;
  protected isDeleting = false;
  protected currentSegment: number = 0;
  protected form = new FormGroup({
    isBusy: new FormControl<boolean>(false),
    enabled: new FormControl<boolean>(false),
    addOption: new FormControl<string>('')
  });
  private readonly allAddOptions: SelectOption[] = [
    {value: '0', text: 'Single Line Text'},
    {value: '1', text: 'Multi Line Text'},
    {value: '2', text: 'Checkboxes'},
    {value: '3', text: 'Radio Buttons'},
    {value: '4', text: 'Dropdown'},
    {value: '5', text: 'Date'},
    {value: '6', text: 'Number'},
    {value: '7', text: 'Time'},
  ];

  protected get addOptions(): SelectOption[] {
    if (this.diaryForm?.diaryType === DiaryType.OnboardingQuestions) {
      return this.allAddOptions.filter(o => ['3', '4', '6'].includes(o.value));
    }
    return this.allAddOptions;
  }

  ngOnInit() {
    // Set up the initial form value after a small delay to ensure the form is fully initialized
    setTimeout(() => {
      this.initialFormValue = this.getFormValueWithoutAddOption();

      // Now subscribe to form value changes
      this.form.valueChanges.subscribe(() => {
        this.checkFormChanges();
      });
    });
  }

  private addOptionSubscription: any;

  ngAfterViewInit() {
    Promise.resolve().then(() => {
      this.loadTrialForm();
      this.setupAddOptionSubscription();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['formId'] && !changes['formId'].firstChange) {
      this.currentSegment = 0;
      this.loadTrialForm();
      this.setupAddOptionSubscription();
    }
  }

  segmentIndexChanged(index: number) {
    this.currentSegment = index;
  }

  /**
   * Sets up the subscription for the addOption form control
   */
  private setupAddOptionSubscription(): void {
    // Unsubscribe from any existing subscription
    this.addOptionSubscription?.unsubscribe();

    // Create new subscription
    this.addOptionSubscription = this.form.get('addOption').valueChanges.subscribe(option => {
      if (option) {
        this.processAddOption(option);
      }
    });
  }

  /**
   * Processes the selected option and resets the dropdown
   * @param option The selected option value
   */
  private processAddOption(option: string): void {
    // Unsubscribe temporarily to prevent the reset from triggering another change
    this.addOptionSubscription?.unsubscribe();

    try {
      // Convert the numeric string to a number and get the corresponding enum key
      const optionNumber = Number(option);
      const enumKey = DiaryFormElementType[optionNumber];

      if (enumKey) {
        this.onAddQuestion(optionNumber as DiaryFormElementType);
      } else {
        console.warn('Invalid option selected:', option);
      }
    } catch (error) {
      console.error('Error processing option:', option, error);
    } finally {
      // Reset the control value without emitting events
      this.resetAddOptionDropdown();

      // Resubscribe for the next selection
      this.setupAddOptionSubscription();
    }
  }

  /**
   * Resets the add option dropdown to its initial state
   */
  private resetAddOptionDropdown(): void {
    this.form.get('addOption').setValue('', {emitEvent: false});
    this.addQuestionDropdown?.reset();
  }

  ngOnDestroy() {
    // Clean up the subscription when the component is destroyed
    this.addOptionSubscription?.unsubscribe();
  }

  onAddQuestion(type: DiaryFormElementType) {
    // Create a new array reference to trigger change detection
    const currentElements = [...(this.diaryForm.elements || [])];

    // Add the new element
    switch (type) {
      case DiaryFormElementType.SingleLineText:
        this.diaryForm.addSingleLineTextElement();
        break;
      case DiaryFormElementType.MultiLineText:
        this.diaryForm.addMultiLineTextElement();
        break;
      case DiaryFormElementType.Date:
        this.diaryForm.addDateElement();
        break;
      case DiaryFormElementType.Time:
        this.diaryForm.addTimeElement();
        break;
      case DiaryFormElementType.Radio:
        this.diaryForm.addRadioElement();
        break;
      case DiaryFormElementType.Checkbox:
        this.diaryForm.addCheckboxElement();
        break;
      case DiaryFormElementType.Dropdown:
        this.diaryForm.addDropdownElement();
        break;
      case DiaryFormElementType.Numeric:
        this.diaryForm.addNumericElement();
        break;
    }

    // Force change detection by creating a new array reference
    this.diaryForm.elements = [...this.diaryForm.elements];

    // Update completion warning for the newly added element
    const newElement = this.diaryForm.elements[this.diaryForm.elements.length - 1];
    this.updateCompletionWarning(newElement);

    // Check for changes after adding a question
    this.checkFormChanges();

    // Scroll to the bottom so the new element is visible
    setTimeout(() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }));
  }

  /**
   * Gets the current form value excluding the 'addOption' control
   */
  private getFormValueWithoutAddOption(): any {
    if (!this.form) return null;

    const formValue = {...this.form.value};
    delete formValue.addOption; // Remove addOption from the form value
    return formValue;
  }

  /**
   * Compares current form value with initial value to detect changes
   */
  private checkFormChanges(): void {
    if (!this.initialFormValue) return;

    const currentValue = this.getFormValueWithoutAddOption();
    const formHasChanges = JSON.stringify(currentValue) !== JSON.stringify(this.initialFormValue);

    // Check if diary form has changes
    const diaryFormHasChanges = this.hasDiaryFormChanged();

    // Update formHasChanges if either the form or diary form has changes
    const hasChanges = formHasChanges || diaryFormHasChanges;

    if (this.formHasChanges !== hasChanges) {
      this.formHasChanges = hasChanges;
      this.formChanged.emit(hasChanges);
    }
  }

  /**
   * Checks if the diary form has been modified since it was loaded
   */
  private hasDiaryFormChanged(): boolean {
    if (!this.initialDiaryFormValue || !this.diaryForm) return false;

    // Create a deep copy of the current diary form for comparison
    const currentDiaryForm = JSON.parse(JSON.stringify(this.diaryForm));

    // Compare the current diary form with the initial state
    return JSON.stringify(currentDiaryForm) !== JSON.stringify(this.initialDiaryFormValue);
  }

  /**
   * Resets the form change tracking with the current form values
   */
  resetFormTracking(forceReset: boolean = false): void {
    const newInitialValue = this.getFormValueWithoutAddOption();
    // Only update if the value has actually changed to prevent unnecessary change detection
    if (JSON.stringify(newInitialValue) !== JSON.stringify(this.initialFormValue) || forceReset) {
      this.initialFormValue = newInitialValue;
      // Store the current state of the diary form
      this.initialDiaryFormValue = this.diaryForm ? JSON.parse(JSON.stringify(this.diaryForm)) : null;
      this.formHasChanges = false;
      this.formChanged.emit(false);
    }
  }

  loadTrialForm() {
    if (!this.formId) {
      LogHelper.log('Form Id not present, cannot load diary form');
      return;
    }

    this.isLoading = true;
    this._diaryFormService.getById(this.formId).subscribe({
      next: form => {
        // Create a new instance to ensure we have a clean object
        this.completionWarningElements.clear();
        this.diaryForm = new DiaryForm(form);
        this.diaryForm.elements.forEach(e => this.updateCompletionWarning(e));
        this.isLoading = false;

        // Patch the form value
        this.form.patchValue({
          enabled: form.enabled,
          isBusy: false
        }, {emitEvent: false}); // Don't trigger valueChanges

        // Set initial form values immediately
        this.initialFormValue = this.getFormValueWithoutAddOption();
        this.initialDiaryFormValue = this.diaryForm ? JSON.parse(JSON.stringify(this.diaryForm)) : null;
        this.formHasChanges = false;
        this.formChanged.emit(false);
      },
      error: err => {
        LogHelper.log(err);
        this.isLoading = false;
        this._alertService.showWarningAlert('There was an error loading the diary form.');
      }
    });
  }

  elementEditable(element: DiaryFormElement): boolean {
    if (!element.id) { return true; }
    return !this.diaryForm.elementIdsWithEntries.includes(element.id);
  }

  onMoveElementUp(index: number) {
    this.diaryForm.moveElementUp(index);
    this.resetVisibilityIfFirst(index - 1);
    this.checkFormChanges();
  }

  onMoveElementDown(index: number) {
    this.diaryForm.moveElementDown(index);
    this.resetVisibilityIfFirst(index);
    this.checkFormChanges();
  }

  private resetVisibilityIfFirst(newIndex: number) {
    if (newIndex === 0 && this.diaryForm.elements.length > 0 && !this.diaryForm.elements[0].defaultVisible) {
      const element = this.diaryForm.elements[0];
      element.defaultVisible = true;
      element.visibilityOperator = null;
      element.visibilityConditions = [];
    }
  }

  onRemoveElement(index: number) {
    this.confirmRemoveElement.showConfirmation(index.toString(), 'Remove Element?', 'Are you sure you want to remove this element?<br>The action cannot be undone', 'Yes, Remove')
  }

  onRemoveMessageFromElement(elementIndex: number, messageIndex: number): void {
    this.diaryForm.elements[elementIndex].messages.splice(messageIndex, 1);
    this.checkFormChanges();
  }

  onConfirmRemoveElement(index: string) {
    this.completionWarningElements.delete(this.diaryForm.elements[+index]);
    this.diaryForm.removeElementAt(+index);
    this.resetVisibilityIfFirst(0);
    this.checkFormChanges();
  }

  onManageElement(element: DiaryFormElement) {
    const isOnboardingQuestions = this.diaryForm?.diaryType === DiaryType.OnboardingQuestions;
    this.elementSettingsModal.openManageElementModal(element, this.elementEditable(element), isOnboardingQuestions);
  }

  onElementUpdated(updatedElement: DiaryFormElement) {
    this.updateCompletionWarning(updatedElement);
    this.formHasChanges = true;
    this.formChanged.emit(true);
  }

  private updateCompletionWarning(element: DiaryFormElement): void {
    const supported = [DiaryFormElementType.Radio, DiaryFormElementType.Dropdown, DiaryFormElementType.Numeric];
    if (this.diaryForm?.diaryType !== DiaryType.OnboardingQuestions || !supported.includes(element.elementType)) {
      this.completionWarningElements.delete(element);
      return;
    }
    if (!element.completionOptionId && !element.completionValue) {
      this.completionWarningElements.add(element);
    } else {
      this.completionWarningElements.delete(element);
    }
  }

  onHandleEditMessage(elementIndex: number, messageIndex: number) {
    this.messageModal.edit(elementIndex, this.diaryForm.elements[elementIndex], this.diaryForm.elements[elementIndex].messages[messageIndex]);
  }

  onHandleAddMessageToElement(index: number) {
    this.messageModal.add(index, this.diaryForm.elements[index]);
  }

  onHandleMessageAddedToElement(data: {elementIndex: number, message: DiaryFormElementMessage}) {
    this.diaryForm.elements[data.elementIndex].messages.push(data.message);
    this.sortElementMessages(data.elementIndex);
    this.checkFormChanges();
  }

  onHandleMessageUpdated(data: {elementIndex: number, message: DiaryFormElementMessage}) {
    const messageIndex = this.diaryForm.elements[data.elementIndex].messages.findIndex(m => m.id === data.message.id);
    if (messageIndex === -1)
      return;

    console.log(data.message.patientChoice);

    this.diaryForm.elements[data.elementIndex].messages[messageIndex] = data.message;
    this.sortElementMessages(data.elementIndex);
    this.checkFormChanges();
  }

  onHandleVisibilityEdit(index: number) {
    this.visibilityModal.open(index, this.diaryForm.elements[index], this.diaryForm.elements);
  }

  onHandleVisibilityApplied(data: {elementIndex: number, defaultVisible: boolean, visibilityOperator: number | null, visibilityConditions: DiaryFormElementVisibilityCondition[]}) {
    this.diaryForm.elements[data.elementIndex].defaultVisible = data.defaultVisible;
    this.diaryForm.elements[data.elementIndex].visibilityOperator = data.visibilityOperator;
    this.diaryForm.elements[data.elementIndex].visibilityConditions = data.visibilityConditions;
    this.checkFormChanges();
  }

  private sortElementMessages(elementIndex: number): void {
    const styleOrder: Record<string, number> = {
      'success': 0,
      'danger': 1,
      'info': 2
    };

    this.diaryForm.elements[elementIndex].messages.sort((a, b) => {
      // Active first
      if (a.active !== b.active) {
        return a.active ? -1 : 1;
      }
      // Then by style order
      const aOrder = styleOrder[a.style] ?? 999;
      const bOrder = styleOrder[b.style] ?? 999;
      return aOrder - bOrder;
    });
  }

  onConfigUpdated(newName: string) {
    this.checkFormChanges();
  }

  onRulesUpdated() {
    this.checkFormChanges();
  }

  hasUnsavedOptions() {
    if (!this.diaryForm || !this.diaryForm.elements)
      return false;

    for (const element of this.diaryForm.elements) {
      if (element.options && element.options.length > 0) {
        for (const option of element.options) {
          if (!option.id) {
            return true;
          }
        }
      }
    }

    return false;
  }

  isAddRuleButtonDisabled() {
    if (!this.diaryForm || !this.diaryForm.elements || this.hasUnsavedOptions())
      return true;

    if (this.diaryForm.elements.length === 0)
      return true;

    return false;
  }

  onRemoveDiary() {
    this.confirmRemoveDiary.showConfirmation(this.formId, 'Delete eDiary?', 'Are you sure you want to delete this eDiary?', 'Yes', 'No');
  }

  onConfirmRemoveDiary(formId: string) {
    this.isDeleting = true;
    this._diaryFormService.delete(formId).subscribe({
      next: () => {
        this.isDeleting = false;
        this.diaryDeleted.emit(formId);
      },
      error: err => {
        this.isDeleting = false;
        this._alertService.showErrorAlert(err);
      }
    });
  }

  onSave(): void {
    this.form.patchValue({isBusy: true});

    // Map form to diary object
    this.diaryForm.enabled = this.form.get('enabled').value;

    this._diaryFormService.saveById(this.formId, this.diaryForm).subscribe({
      next: (form)=> {
        this.diaryForm = form;
        this.form.patchValue({isBusy: false});
        this._alertService.showSuccessAlert('Form successfully updated.');
        this.diaryRenamed.emit(this.diaryForm.name);
        this.resetFormTracking(true);
      },
      error: err => {
        LogHelper.log(err);
        this.form.patchValue({isBusy: false});
        this._alertService.showErrorAlert(err);
      }
    });

  }
}
